/**
 * VRG-720 -- the Web Contact Picker API, typed locally (it is not in lib.dom).
 *
 * ## Where it exists
 *
 * Chrome on ANDROID only. iOS Safari has it behind an experimental flag that is off by
 * default, and desktop browsers do not ship it. So this is an enhancement, never a
 * dependency: `isContactPickerSupported()` gates the button, and on iPhone nothing in the UI
 * references the picker at all (the visitor gets autofill via `autocomplete="name"` /
 * `"email"` instead).
 *
 * ## Why feature-detect BOTH halves
 *
 * `'contacts' in navigator` alone is true in some embedded WebViews that expose the manager
 * object but not the constructor. The spec's own detection example checks both.
 *
 * ## Privacy shape
 *
 * `select` requires a user gesture and shows the browser's own picker, so the visitor picks
 * exactly one contact and only the `name` and `email` properties we ask for are returned.
 * `multiple: false` -- we want the visitor's OWN card, not a list.
 */

interface ContactPickerResult {
  name?: string[];
  email?: string[];
}

interface ContactsManagerLike {
  select(
    properties: string[],
    options?: { multiple?: boolean },
  ): Promise<ContactPickerResult[]>;
}

export interface PickedContact {
  name?: string;
  email?: string;
}

export const isContactPickerSupported = (): boolean => {
  try {
    return (
      typeof navigator !== 'undefined' &&
      typeof window !== 'undefined' &&
      'contacts' in navigator &&
      'ContactsManager' in window
    );
  } catch {
    return false;
  }
};

/**
 * Ask the browser to pick ONE contact and return the first name and first email on it.
 *
 * Resolves `null` when the visitor dismisses the picker (the API rejects with an
 * `AbortError`/`InvalidStateError` for that and for a missing gesture) or when the API is
 * unsupported. Callers treat `null` as "nothing happened", never as an error to show.
 */
export const pickContact = async (): Promise<PickedContact | null> => {
  if (!isContactPickerSupported()) return null;
  try {
    const contacts = (navigator as unknown as { contacts: ContactsManagerLike }).contacts;
    const [picked] = await contacts.select(['name', 'email'], { multiple: false });
    if (!picked) return null;
    const name = picked.name?.find(value => value?.trim())?.trim();
    const email = picked.email?.find(value => value?.trim())?.trim();
    if (!name && !email) return null;
    return { name, email };
  } catch {
    return null;
  }
};
